import React, { useState, useEffect } from 'react';
import API_URL from '../config';
import { useToast, parseApiError } from '../context/ToastContext';

function PromptRegistry({ token }) {
  const toast = useToast();
  const [prompts, setPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [versions, setVersions] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [compareVersions, setCompareVersions] = useState(null);
  const [form, setForm] = useState({ prompt_name: '', content: '', description: '' });
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchPrompts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/protected/prompts`, { headers });
      if (res.ok) setPrompts((await res.json()).prompts || []);
    } catch (e) {}
  };

  useEffect(() => { fetchPrompts(); }, [token]);

  const openPrompt = async (name) => {
    try {
      const res = await fetch(`${API_URL}/api/protected/prompts/${encodeURIComponent(name)}/versions`, { headers });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to load prompt'));
      const data = await res.json();
      setVersions(data.versions || []);
      setSelectedPrompt(name);
      setCompareVersions(null);
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const createVersion = async () => {
    if (!form.prompt_name.trim() && !selectedPrompt) { toast('Prompt name is required', 'warning'); return; }
    if (!form.content.trim()) { toast('Content is required', 'warning'); return; }
    try {
      const res = await fetch(`${API_URL}/api/protected/prompts`, {
        method: 'POST', headers,
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to save prompt'));
      toast('Prompt version saved', 'success');
      setShowCreate(false);
      setForm({ prompt_name: '', content: '', description: '' });
      fetchPrompts();
      if (selectedPrompt) openPrompt(selectedPrompt);
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const activateVersion = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/protected/prompts/versions/${id}/activate`, { method: 'PUT', headers });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to activate version'));
      toast('Version activated', 'success');
      openPrompt(selectedPrompt);
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const deletePrompt = async (name) => {
    if (!window.confirm(`Delete all versions of "${name}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/protected/prompts/${encodeURIComponent(name)}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to delete prompt'));
      toast('Prompt deleted', 'success');
      setSelectedPrompt(null);
      fetchPrompts();
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  if (selectedPrompt) {
    const activeVersion = versions.find(v => v.is_active);
    return (
      <>
        <div className="page-header">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button className="btn-ghost" onClick={() => setSelectedPrompt(null)}>&larr; Back</button>
              <div className="page-title">{selectedPrompt}</div>
              <span className="badge badge-info">{versions.length} versions</span>
            </div>
            <div className="page-subtitle">Active: v{activeVersion?.version || '-'}</div>
          </div>
          <div className="page-actions">
            <button className="btn-primary btn-sm" onClick={() => {
              setForm({ prompt_name: selectedPrompt, content: versions[0]?.content || '', description: '' });
              setShowCreate(true);
            }}>New Version</button>
            <button className="btn-danger btn-sm" onClick={() => deletePrompt(selectedPrompt)}>Delete</button>
          </div>
        </div>
        <div className="page-body">
          {showCreate && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-title" style={{ marginBottom: 12 }}>New Version</div>
              <div className="form-group">
                <label>Description</label>
                <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What changed?" />
              </div>
              <div className="form-group">
                <label>Content</label>
                <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={10} style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-primary" onClick={createVersion}>Save Version</button>
                <button className="btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
              </div>
            </div>
          )}

          {compareVersions && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header">
                <div className="card-title">Compare v{compareVersions[0].version} vs v{compareVersions[1].version}</div>
                <button className="btn-ghost btn-sm" onClick={() => setCompareVersions(null)}>Close</button>
              </div>
              <div className="diff-viewer">
                <div className="diff-pane">
                  <div className="diff-pane-header">v{compareVersions[0].version}</div>
                  {compareVersions[0].content}
                </div>
                <div className="diff-pane">
                  <div className="diff-pane-header">v{compareVersions[1].version}</div>
                  {compareVersions[1].content}
                </div>
              </div>
            </div>
          )}

          <div className="card">
            <table>
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {versions.map((v, i) => (
                  <tr key={v.id}>
                    <td>
                      <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>v{v.version}</span>
                    </td>
                    <td>{v.description || '-'}</td>
                    <td>
                      {v.is_active ? <span className="badge badge-success">Active</span> : <span className="badge badge-neutral">Inactive</span>}
                    </td>
                    <td className="text-sm">{new Date(v.created_at).toLocaleString()}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {!v.is_active && <button className="btn-ghost btn-sm" onClick={() => activateVersion(v.id)}>Activate</button>}
                        {i < versions.length - 1 && (
                          <button className="btn-ghost btn-sm" onClick={() => setCompareVersions([v, versions[i + 1]])}>Compare</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {activeVersion && (
            <div className="card">
              <div className="card-header">
                <div className="card-title">Active Content (v{activeVersion.version})</div>
              </div>
              <div className="code-block">{activeVersion.content}</div>
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">Prompt Registry</div>
          <div className="page-subtitle">Version, compare, and rollback prompts</div>
        </div>
        <button className="btn-primary" onClick={() => { setForm({ prompt_name: '', content: '', description: '' }); setShowCreate(true); }}>New Prompt</button>
      </div>
      <div className="page-body">
        {showCreate && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-title" style={{ marginBottom: 12 }}>Create Prompt</div>
            <div className="form-row">
              <div className="form-group">
                <label>Prompt Name</label>
                <input value={form.prompt_name} onChange={e => setForm({ ...form, prompt_name: e.target.value })} placeholder="e.g. system-prompt" />
              </div>
              <div className="form-group">
                <label>Description</label>
                <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What is this prompt for?" />
              </div>
            </div>
            <div className="form-group">
              <label>Content</label>
              <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={8} placeholder="Your prompt content..." style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-primary" onClick={createVersion}>Create</button>
              <button className="btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
            </div>
          </div>
        )}

        {prompts.length === 0 && !showCreate ? (
          <div className="card">
            <div className="empty-state">
              <div className="empty-state-icon">&para;</div>
              <h3>No prompts registered</h3>
              <p>Store and version your prompts to track changes, compare versions, and roll back when needed.</p>
              <button className="btn-primary" onClick={() => setShowCreate(true)}>Create First Prompt</button>
            </div>
          </div>
        ) : (
          <div className="card">
            <table>
              <thead>
                <tr>
                  <th>Prompt Name</th>
                  <th>Latest Version</th>
                  <th>Active Version</th>
                  <th>Total Versions</th>
                  <th>Last Updated</th>
                </tr>
              </thead>
              <tbody>
                {prompts.map(p => (
                  <tr key={p.prompt_name} className="clickable-row" onClick={() => openPrompt(p.prompt_name)}>
                    <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{p.prompt_name}</td>
                    <td>v{p.latest_version}</td>
                    <td>{p.active_version > 0 ? <span className="badge badge-success">v{p.active_version}</span> : <span className="badge badge-neutral">None</span>}</td>
                    <td>{p.total_versions}</td>
                    <td className="text-sm">{new Date(p.updated_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

export default PromptRegistry;
